<template>
    <Head title="Privacy Policy" />
    <div class="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div
            class="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-gray-200 mb-8 max-w-[60rem] mx-auto py-10 px-5"
        >
            <div
                class="mx-auto max-w-[50rem] prose-sm prose prose-slate prose-a:font-semibold prose-a:text-orange-400 hover:prose-a:text-orange-700"
            >
                <section class="mb-8 text-center">
                    <h1
                        class="text-2xl md:text-3xl text-gray-800 font-bold mb-2"
                    >
                        My10x: Privacy Policy
                    </h1>
                    <p>
                        <strong>Last Modified:</strong>
                        <br />
                        October 1, 2022&nbsp;
                    </p>
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        Introduction
                    </h3>

                    <p>
                        Aethereal, LLC (
                        <strong>&ldquo;Aethereal, LLC</strong>
                        &rdquo;,
                        <strong>&ldquo;Company&rdquo;</strong>
                        ,
                        <strong>&ldquo;we&rdquo;</strong>
                        ,
                        <strong>&ldquo;our&rdquo;</strong>
                        or
                        <strong>&ldquo;us&rdquo;</strong>
                        ) respects your privacy and is committed to protecting
                        it through our compliance with this policy. This policy
                        describes the types of information we may collect from
                        you or that you may provide when you visit the website
                        10xtravel.com and MileValue.com (together our &ldquo;
                        <strong>Website</strong>
                        &rdquo;) or that we collect from third parties, and our
                        practices for collecting, using, maintaining,
                        protecting, and disclosing that information. This policy
                        also serves to notify you of your rights to the
                        information.&nbsp;
                    </p>
                    <p>
                        This policy applies to the information we collect in the
                        following ways:
                    </p>

                    <ul>
                        <li>On this Website</li>
                        <li>Through our mobile application</li>
                        <li>Through our desktop application</li>
                        <li>
                            In email, text, and other electronic messages
                            between you and this Website
                        </li>
                    </ul>

                    <p>It does not apply to information collected by:</p>
                    <ul>
                        <li>
                            Any third party, including our affiliates or
                            subsidiaries, including through any application or
                            content that may link to or be accessible from or on
                            the Website.
                        </li>
                    </ul>
                    <p>
                        Please read this policy carefully to understand our
                        policies and practices regarding your information and
                        how we will treat it. This policy may change from time
                        to time (see Changes to Our Privacy Policy).&nbsp;
                    </p>
                    <p>
                        Your continued use of this Website after we make changes
                        is deemed to be acceptance of those changes. Please
                        check the policy periodically for updates.
                    </p>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">Notice</h3>
                    <p>
                        We collect several types of information from and about
                        users of our Website, including information:
                    </p>
                    <ol>
                        <li>
                            By which you or your household may be personally
                            identified, directly or indirectly, such as name,
                            postal address, e-mail address, or telephone number
                            ("
                            <strong>Personal Information</strong>
                            ").
                        </li>
                        <li>
                            Personal Information that has been voluntarily made
                            public by you.
                        </li>
                        <li>
                            Lawfully obtained truthful information that is of
                            public concern.
                        </li>
                        <li>
                            That is about you individually but does not identify
                            you; and/or
                        </li>
                        <li>
                            About your internet connection, the equipment you
                            use to access our Website, mobile app, desktop app,
                            and usage details.
                        </li>
                    </ol>
                    <p>We collect this information:</p>

                    <ul>
                        <li>Directly from you when you provide it to us.</li>
                        <li>Automatically as you navigate through the site.</li>
                        <li>
                            From third parties, for example, our business
                            partners.
                        </li>
                    </ul>

                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        Information You Provide To Us
                    </h3>
                    <p>
                        The information we collect on or through our Website may
                        include:
                    </p>
                    <ol>
                        <li>
                            Information that you provide to us directly, which
                            may involve filling in forms on our Website,
                            including:
                            <ol>
                                <li>Your name/alias</li>
                                <li>Your email&nbsp;&nbsp;</li>
                                <li>Your mailing and/or billing address</li>
                                <li>
                                    Information relating to your
                                    creditworthiness, assets, income, or
                                    liabilities
                                </li>
                            </ol>
                        </li>
                        <li>
                            Information when you report a problem with our
                            Website.
                        </li>
                        <li>
                            Records and copies of your correspondence (including
                            email addresses), if you contact us.
                        </li>
                        <li>
                            Information you provide us when entering into a
                            contest, promotion, or sweepstakes sponsored by us.
                        </li>
                        <li>
                            Information you provide us when completing a
                            voluntary survey.
                        </li>
                        <li>
                            Details of your transactions made through our
                            Website, as well as details relating to the
                            fulfillment of the transactions. This may include
                            your financial information.
                        </li>
                        <li>
                            The search queries that you make on our website's
                            integrated search feature.
                        </li>
                        <li>
                            You also may provide information to be published or
                            displayed (hereinafter, "
                            <strong>posted</strong>
                            ") on public areas of the Website, or transmitted to
                            other users of the Website or third parties
                            (collectively, "
                            <strong>User Contributions</strong>
                            ").
                            <p>
                                Your User Contributions are posted on and
                                transmitted to others at your own risk. Although
                                you may set certain privacy settings for such
                                information by logging into your account
                                profile, please be aware that no security
                                measures are perfect or impenetrable.
                                Additionally, we cannot control the actions of
                                other users of the Website with whom you may
                                choose to share your User Contributions.
                                Therefore, we cannot and do not guarantee that
                                your User Contributions will not be viewed by
                                unauthorized persons.
                            </p>
                        </li>
                    </ol>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        How we use this information:
                    </h3>
                    <ul>
                        <li>To present our Website and its contents to you</li>
                        <li>For analytics</li>
                        <li>For marketing or advertising</li>
                        <li>
                            Participation in surveys, contests, sweepstakes, or
                            promotions
                        </li>
                        <li>
                            To provide or process information, products, or
                            services that you request from us
                        </li>
                        <li>To process payments</li>
                        <li>
                            To fulfill any other purpose for which you provide
                            it
                        </li>
                        <li>
                            To carry out our obligations and enforce our rights
                            arising from any contracts entered into between you
                            and us, including for billing and collection
                        </li>
                        <li>Auditing transactions entered into by you</li>
                        <li>
                            To notify you about changes to our Website or any
                            products or services we offer or provide through it
                        </li>
                        <li>To enforce our Terms of Use</li>
                        <li>
                            Protecting against malicious, deceptive, fraudulent,
                            or illegal activity, and prosecuting those
                            responsible for such activities
                        </li>
                        <li>Providing customer service</li>
                        <li>Verifying customer information</li>
                        <li>For any other purpose that you consent to.</li>
                    </ul>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        Information We Collect Automatically
                    </h3>
                    <p>
                        There is certain information that we will collect
                        automatically as you navigate through and interact with
                        our website, this includes:
                    </p>
                    <ol>
                        <li>
                            Details of your visits to our Website, including:
                            <ol>
                                <li>Communication data&nbsp;</li>
                                <li>Log data</li>
                                <li>Traffic data</li>
                                <li>Unique personal identifiers</li>
                                <li>The date and time of access, and</li>
                                <li>
                                    The resources that you access and use on our
                                    Website.
                                </li>
                            </ol>
                        </li>
                        <li>
                            We also collect information relating to your
                            browsing activity on third party websites, which may
                            give us an idea of your interests, and shopping
                            habits, and allow us to make targeted advertising.
                            If you would like to opt-out of targeted
                            advertising, information is provided in the "Your
                            Rights" section of this policy.
                        </li>
                        <li>
                            Information about your computer, internet
                            connection, or electronic network activity,
                            including your:&nbsp;
                            <ol>
                                <li>IP address</li>
                                <li>Operating system</li>
                                <li>Browser type and browser version</li>
                                <li>Browsing/search history</li>
                                <li>Device information</li>
                                <li>Screen resolution</li>
                                <li>Location data</li>
                                <li>Language</li>
                                <li>Time zone</li>
                            </ol>
                        </li>
                        <li>
                            The technologies that we use to track and collect
                            this data includes:
                            <ol>
                                <li>Cookies (see Cookie Policy)</li>
                                <li>Retargeting and/or landing page pixels</li>
                                <li>
                                    Conversion pixels, which may be placed in
                                    emails sent by us
                                </li>
                                <li>Information fingerprinting</li>
                                <li>
                                    We also collect similar information from
                                    third parties and affiliates
                                </li>
                            </ol>
                        </li>
                    </ol>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        Cookie Policy
                    </h3>
                    <p>
                        Cookies are small files of text delivered to your
                        browser by a website you visit. They help that website
                        identify your computer and remember information about
                        your visit, making it easier to revisit the site and
                        making the site more useful to you. There are other
                        types of tracking technologies, such as pixels and
                        beacons. Below is a description of how various Cookies
                        and other automatic tracking technologies may be used, a
                        disclosure as to whether we use them, and your ability
                        to control these technologies.&nbsp;
                    </p>
                    <p><strong>Essential Cookies</strong></p>
                    <p>
                        Essential cookies or strictly necessary cookies are
                        cookies that are essential for a website to function
                        correctly. These cookies are necessary to provide an
                        online service on a website or used solely to carry out
                        or facilitate the transmission of communications over a
                        network.
                    </p>
                    <p>
                        We collect essential cookies that are necessary for our
                        Website to operate properly.
                    </p>
                    <p><strong>Analytics Cookies</strong></p>
                    <p>
                        Cookies and other technologies used for analytics help
                        collect data that allows websites and services to
                        understand how users interact with a particular website
                        or service. This will enable companies to improve their
                        services and content and build better features to
                        enhance your experience.
                    </p>
                    <p>
                        We and third parties collect analytics cookies on our
                        Website. You can opt-out of the use of third party
                        analytics cookies by following our opt-out procedures
                        outlined in the "Your Rights" section of our Privacy
                        Policy.
                    </p>
                    <p><strong>Advertising Cookies</strong></p>
                    <p>
                        Advertising cookies are used to (1) deliver
                        advertisements more relevant to you and your interests;
                        (2) limit the number of times you see an advertisement;
                        (3) help measure the effectiveness of the advertising
                        campaign; (4) retarget you to our Website, and (5)
                        understand people&rsquo;s behavior after they view an
                        advertisement. They are usually placed on behalf of
                        advertising networks with the site operator&rsquo;s
                        permission. They remember that you have visited a site,
                        and often they will be linked to site functionality
                        provided by the other organization. This may impact the
                        content and messages you see on other websites you
                        visit.
                    </p>
                    <p>
                        We and third parties collect cookies for advertising
                        purposes on our Website. You can opt-out of the use of
                        third party analytics cookies by following our opt-out
                        procedures outlined in the "Your Rights" section of our
                        Privacy Policy.
                    </p>
                    <p><strong>Third Party Cookie Disclosures</strong></p>
                    <p>
                        Some cookies, web beacons and other tracking and storage
                        technologies that we use are from third party companies
                        (third party cookies) to provide us with web analytics
                        and intelligence about our sites which may also be used
                        to provide measurement services and target ads. These
                        companies use programming code to collect information
                        about your interaction with our sites, such as the pages
                        you visit, the links you click on and how long you are
                        on our sites. This code is only active while you are on
                        our Website. For more information on how these companies
                        collect and use the information on our behalf, please
                        refer to their privacy policies: Facebook, Google, and
                        Snapchat.
                    </p>
                    <p><strong>Managing Cookies</strong></p>
                    <p>
                        Most browsers allow you to manage how cookies are set
                        and used as you&rsquo;re browsing, and allow you to
                        clear cookies and browsing data. Also, your browser may
                        have settings letting you manage cookies on a
                        site-by-site basis.
                    </p>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        Email Engagement Tracking
                    </h3>
                    <p>
                        When we send you an email — including account
                        notifications, product updates, billing receipts, and
                        marketing messages — we use our email service provider
                        (Mailgun) to record engagement events such as
                        deliveries, opens, clicks on links within the email,
                        bounces, spam complaints, and unsubscribes. This is
                        standard practice across email providers and helps us
                        improve deliverability, measure the effectiveness of
                        the messages we send, and detect issues such as
                        invalid email addresses.
                    </p>
                    <p>
                        To enable this, our emails contain a small invisible
                        tracking pixel and the links inside them are routed
                        through Mailgun's click-tracking service. When you
                        open an email or click a link, we may collect:
                    </p>
                    <ul class="list-disc ml-6 my-3">
                        <li>Your IP address at the time of the open or click</li>
                        <li>
                            Approximate geographic location derived from your
                            IP address (city or region level)
                        </li>
                        <li>
                            Device, operating system, and email client
                            information from your user agent
                        </li>
                        <li>
                            The specific link clicked, where applicable
                        </li>
                    </ul>
                    <p>
                        We retain these engagement records for up to 180 days,
                        after which they are automatically deleted. You can
                        opt out of non-transactional email at any time by
                        clicking the unsubscribe link at the bottom of any
                        marketing message; transactional emails (such as
                        verification codes, password resets, billing receipts,
                        and security alerts) will continue to be sent because
                        they are required to operate your account.
                    </p>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        Business Justification
                    </h3>
                    <p>
                        The information we collect automatically may include
                        personal information, or we may maintain it or associate
                        it with personal information we collect in other ways or
                        receive from third parties.&nbsp;
                    </p>
                    <p>How we use this information we collect automatically:</p>
                    <ul>
                        <li>To present our Website and its contents to you</li>
                        <li>For analytics</li>
                        <li>For marketing or advertising</li>
                        <li>
                            Participation in surveys, contests, sweepstakes, or
                            promotions
                        </li>
                        <li>
                            To provide or process information, products, or
                            services that you request from us
                        </li>
                        <li>To process payments</li>
                        <li>
                            To fulfill any other purpose for which you provide
                            it
                        </li>
                        <li>
                            To carry out our obligations and enforce our rights
                            arising from any contracts entered into between you
                            and us, including for billing and collection
                        </li>
                        <li>Auditing transactions entered into by you</li>
                        <li>
                            To notify you about changes to our Website or any
                            products or services we offer or provide through it
                        </li>
                        <li>To enforce our Terms of Use</li>
                        <li>
                            Protecting against malicious, deceptive, fraudulent,
                            or illegal activity, and prosecuting those
                            responsible for such activities
                        </li>
                        <li>Providing customer service</li>
                        <li>Verifying customer information</li>
                        <li>For any other purpose with your consent</li>
                    </ul>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        Sensitive Personal Data
                    </h3>
                    <p>
                        The California Privacy Rights Act identifies the
                        following as "
                        <strong>Sensitive Personal Information</strong>
                        ": personal information that reveals (1) a
                        consumer&rsquo;s Social Security or other state
                        identification number such as a driver's license; (2) a
                        consumer&rsquo;s account log-in, financial account,
                        debit card, or credit card number in combination with
                        any required security or access code, password, or
                        credentials allowing access to an account; (3)
                        consumer&rsquo;s geolocation; (4) consumer&rsquo;s
                        racial or ethnic origin, (5) religious or philosophical
                        beliefs, (6) union membership; (7) the contents of a
                        consumer&rsquo;s mail, email, or text messages, unless
                        the business is the intended recipient of the
                        communication; and (8) consumer&rsquo;s genetic data.
                    </p>
                    <p>
                        We collect information of yours that is considered to be
                        Sensitive Personal Information to infer characteristics
                        about you. However, our use is either necessary to
                        perform our services requested by you, necessary to help
                        ensure security or integrity, necessary to perform
                        services on behalf of our business (such as fulfilling
                        orders, processing payments, or verifying customer
                        information), or necessary to verify or maintain the
                        quality or safety of a service or device that is owned
                        or controlled by us. Because we are only using your data
                        for these necessary processes, we are not compelled to
                        limit our use.
                    </p>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        How Your Information May be Used With Third Parties
                    </h3>
                    <p>
                        Any aggregated data or any information which does not
                        identify any individual or de-identified data may be
                        disclosed to third parties by us without any
                        restrictions. We are not obligated to first receive
                        consent before disclosing any such data or information.
                        Any information that you have made public before we
                        collected it may also be shared without
                        restriction.&nbsp;
                    </p>
                    <p>
                        We may use your information and data for direct
                        marketing purposes. This may include us using your
                        information to contact you about our own or third
                        parties' goods or services that they or we think may be
                        of interest to you. If you would like to opt-out, follow
                        the instructions provided in the "Your Rights" section.
                    </p>
                    <p>
                        We will also disclose your information for analytics
                        purposes.
                    </p>
                    <ol>
                        <li>
                            <strong>Third Party Tracking</strong>
                            <br />
                            We do not control these third parties' tracking
                            technologies or how they may be used. If you have
                            any questions about an advertisement or other
                            targeted content, you should contact the responsible
                            provider directly.
                        </li>
                        <li>
                            <strong>Third Party Disclosures</strong>
                            <br />
                            We may disclose personal information that we
                            collect, or you provide as described in this privacy
                            policy:
                            <ol>
                                <li>To our subsidiaries and affiliates</li>
                                <li>
                                    To contractors, service providers, and other
                                    third parties we use to support our business
                                    and who are bound by contractual obligations
                                    to keep personal information confidential
                                    and use it only for the purposes for which
                                    we disclose it to them.
                                </li>
                                <li>
                                    To a buyer or other successor in the event
                                    of a merger, divestiture, restructuring,
                                    reorganization, dissolution, or other sale
                                    or transfer of some or all of Aethereal's
                                    assets, whether as a going concern or as
                                    part of bankruptcy, liquidation, or similar
                                    proceeding, in which personal information
                                    held by Aethereal about our Website users is
                                    among the assets transferred.
                                </li>
                                <li>
                                    For any other purpose disclosed by us when
                                    you provide the information.
                                </li>
                                <li>For any purpose that you consent to.</li>
                            </ol>
                            <p>
                                We may also disclose your personal information
                                without your consent:
                            </p>
                            <ol>
                                <li>
                                    To comply with any court order, law, or
                                    legal process, including responding to any
                                    government or regulatory request.
                                </li>
                                <li>
                                    For other agreements, including for billing
                                    and collection purposes.
                                </li>
                                <li>
                                    If we believe disclosure is necessary or
                                    appropriate to protect the rights, property,
                                    or safety of Aethereal, our customers, or
                                    others.
                                </li>
                            </ol>
                        </li>
                        <li>
                            <strong>Categories of 3rd party providers</strong>
                            <br />
                            We share your information with the following classes
                            of third parties:
                            <ol>
                                <li>Advertising networks</li>
                                <li>Internet service providers</li>
                                <li>Operating systems and platforms</li>
                                <li>Social networks</li>
                                <li>Customer management systems</li>
                                <li>Data analytics providers</li>
                                <li>Data linking platforms</li>
                                <li>Email marketing vendors</li>
                                <li>Email service providers</li>
                                <li>Financial transactions processors</li>
                                <li>Fraud prevention services vendors</li>
                                <li>Hosting providers</li>
                                <li>
                                    Parties that need to operate the website
                                </li>
                            </ol>
                        </li>
                        <li>
                            <strong>Our Legitimate Interest for Sharing</strong>
                            <br />
                            We will only share your information or data if it is
                            absolutely necessary to accomplish the purposes we
                            collect it, which include:
                            <ol>
                                <li>To manage our interactions with you</li>
                                <li>To prevent or investigate fraud</li>
                                <li>To process transactions</li>
                                <li>To enforce our Terms of Use</li>
                            </ol>
                        </li>
                        <li>
                            All of your data that we share with Service
                            Providers, which is necessary for our Website's
                            business purpose, is disclosed in this Privacy
                            Policy. All agreements we enter into with such
                            Service Provider prohibit the Service Provider from
                            retaining, using, or disclosing the personal
                            information for any purpose other than for the
                            specific purpose of performing the services
                            specified in the contract with the business.
                        </li>
                        <li>
                            Our Website contains links to other sites over which
                            we have no control, and we assume no responsibility
                            for the content, privacy policies, or practices of
                            other third party sites to which you choose to visit
                            from the links hosted on our Website, and such sites
                            are not governed by this Privacy Policy. We
                            encourage you to review the privacy policies of
                            those other sites so you can understand how they
                            collect, use, and share your personal information.
                        </li>
                        <li>
                            Information gathered on a co-branded page may be
                            retained by both the other company participating in
                            the co-branding campaign and us. In that instance,
                            the use of such information by the other company
                            will be subject to the privacy policy of that
                            company. We are not responsible for that
                            company&rsquo;s use of your personal
                            information.&nbsp;
                        </li>
                    </ol>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        Choice/ Consent
                    </h3>
                    <p>
                        Because you are the owner of your personal information
                        and data, you are afforded certain choices concerning
                        the information you provide to us and that we retain. We
                        maintain the following mechanisms for you to control
                        your information:
                    </p>
                    <ol>
                        <li>
                            <strong>
                                Tracking Technologies and Advertising.
                            </strong>
                            <br />
                            We do not control third parties' collection or use
                            of your information to serve interest-based
                            advertising. However, these third parties may
                            provide you with ways to choose not to have your
                            information collected or used in this way. For
                            example, you can opt-out of receiving targeted ads
                            from members of the Network Advertising Initiative
                            ("NAI") on the NAI's website.
                            <br />
                            <br />
                            You can set your browser to refuse all or some
                            browser cookies, or to alert you when cookies are
                            being sent. If you disable or refuse cookies, please
                            note that some parts of this site may then be
                            inaccessible or not function properly.
                        </li>

                        <li>
                            <strong>Right to Know and Be Informed</strong>
                            <br />
                            You have the right to ask us what kind of data we
                            process, why it is necessary for us to collect, whom
                            we intend to share your information with, your
                            rights relating to your data, and inferences we draw
                            from your data. We designed this Privacy Policy to
                            proactively answer all of those questions that you
                            may have, but you have the option to contact us with
                            any questions at contact@10xtravel.com.
                        </li>

                        <li>
                            <strong>Right to Access</strong>
                            <br />
                            You have the right to access the personal data
                            belonging to you that we process. This means that
                            you may request your personal data file that we
                            maintain, which may include the categories and
                            sources of information and specific pieces of
                            information we have collected from you. Upon your
                            request, we will also share any information of yours
                            that we have shared or sold as well as the parties
                            to which we have shared your information. This
                            allows you to become aware of the data that we are
                            actually processing from you and our handling of
                            such data so that you can verify the lawfulness of
                            our processing.
                            <br />
                            <br />
                            We may require verification of your identity before
                            we disclose any of the information that you may
                            request from us.
                            <br />
                            <br />
                            If you would like to exercise this right, please
                            contact: contact@10xtravel.com.
                        </li>

                        <li>
                            <strong>Right to Opt-Out</strong>
                            <br />
                            <ol>
                                <li>
                                    You have the right to object to us selling
                                    your data and third party, for any purpose.
                                    If you are age 16 or older, you have the
                                    right to direct us not to sell your personal
                                    information at any time. We do not sell the
                                    personal information of consumers we
                                    actually know are less than 16 years old,
                                    unless we receive affirmative authorization
                                    from either the consumer who is between 13
                                    and 15 years old, or the parent or guardian
                                    of a consumer less than 13 years old.
                                    Consumers who opt-in to personal information
                                    sales may opt-out of future sales at any
                                    time. You can email contact@10xtravel.com to
                                    opt-out.
                                </li>
                                <li>
                                    Once you make an opt-out request, we will
                                    wait at least twelve (12) months before
                                    asking you to reauthorize personal
                                    information sales. However, you may change
                                    your mind and opt back into personal
                                    information sales at any time by contacting
                                    contact@10xtravel.com.
                                </li>
                                <li>
                                    You can find all categories of third parties
                                    with whom we share personal information in
                                    this Privacy Policy. We do not sell the
                                    personal information that we collect, and we
                                    will not sell it without providing a right
                                    to opt-out. Please review our Cookie Policy
                                    to see how third parties interact with data
                                    on our website.
                                </li>
                            </ol>
                        </li>

                        <li>
                            <strong>Right to Deletion</strong>
                            <br />
                            You have the right to request that we delete any of
                            your personal information that we collect from you
                            and retain, subject to certain exceptions (the "
                            <strong>right to delete</strong>
                            "). Once we receive your request and confirm your
                            identity, we will review your request to see if an
                            exception allowing us to retain the information
                            applies. We may deny your deletion request if
                            retaining the information is necessary for us or our
                            service provider(s) to:
                            <ol>
                                <li>
                                    Complete the transaction for which we
                                    collected the personal information, provide
                                    a good or service that you requested, take
                                    actions reasonably anticipated within the
                                    context of our ongoing business relationship
                                    with you, fulfill the terms of a written
                                    warranty or product recall conducted in
                                    accordance with federal law, or otherwise
                                    perform our contract with you.
                                </li>
                                <li>
                                    Detect security incidents, protect against
                                    malicious, deceptive, fraudulent, or illegal
                                    activity, or prosecute those responsible for
                                    such activities.
                                </li>
                                <li>
                                    Debug products to identify and repair errors
                                    that impair existing intended functionality.
                                </li>
                                <li>
                                    Exercise free speech, ensure the right of
                                    another consumer to exercise their free
                                    speech rights, or exercise another right
                                    provided for by law.
                                </li>
                                <li>
                                    To comply with applicable laws, including,
                                    the California Electronic Communications
                                    Privacy Act (Cal. Penal Code &sect; 1546 et.
                                    seq.).
                                </li>
                                <li>
                                    Enable solely internal uses that are
                                    reasonably aligned with consumer
                                    expectations based on your relationship with
                                    us or that are compatible with the context
                                    that you provided it.
                                </li>
                                <li>Comply with a legal obligation.</li>
                            </ol>

                            We will delete or de-identify personal information
                            not subject to one of these exceptions from our
                            records and will direct our service providers to
                            take similar action.
                        </li>

                        <li>
                            <strong>Right to No Discrimination</strong>
                            <br />
                            We will not discriminate against you if you exercise
                            any of your rights. However, we may charge different
                            prices or provide different levels of services to
                            you if the difference is reasonably related to the
                            value provided to us by your data. Further, we
                            retain the right to provide financial incentives for
                            the ability to collect, sell, share, or retain
                            personal information (if consented to).
                        </li>

                        <li>
                            <strong>Right to Rectification</strong>
                            <br />
                            You have the right to direct us to change or modify
                            the data we have collected from you if you believe
                            that the data is inaccurate or out-of-date. You can
                            modify your information at any time in your User
                            Account. If necessary, you may direct us to modify
                            your information verbally or in writing by
                            contacting: contact@10xtravel.com
                        </li>

                        <li>
                            <strong>Right to Data Portability</strong>
                            <br />
                            You have the right to move, copy, or transfer your
                            personal data in our possession to another
                            controller if we are processing your data after
                            receiving your consent or if the processing is being
                            carried out by automated means. We will deliver your
                            data in a structured, commonly used, and
                            machine-readable format.
                        </li>

                        <li>
                            <strong>Do Not Track Disclosures</strong>
                            <br />
                            We do not support Do Not Track (&ldquo;DNT&rdquo;).
                            Do Not Track is a preference you can set in your web
                            browser to inform websites that you do not want to
                            be tracked.
                        </li>

                        <li>
                            <strong>Direct Marketing</strong>
                            <br />
                            To opt-out of direct marketing, please check the
                            relevant box located on the form on which we collect
                            your data. This could be the website or service
                            registration form or an order form (if applicable).
                            You may also contact contact@10xtravel.com.
                        </li>

                        <li>
                            <strong>Exercising your Rights</strong>
                            <br />
                            To exercise any of the rights provided in this
                            section, please submit a request to:
                            contact@10xtravel.com.
                            <br />
                            Only you, or someone legally authorized to act on
                            your behalf, may make a request to exercise the data
                            owner's rights. You may only request to exercise
                            your rights twice in one 12-month period.
                            <br />
                            Your request must:
                            <ol>
                                <li>
                                    Provide sufficient information to allow us
                                    to reasonably verify that you are the person
                                    about whom we collected personal information
                                    or an authorized representative.
                                </li>
                                <li>
                                    Describe your request with sufficient detail
                                    that allows us to properly understand,
                                    evaluate, and respond to it.
                                </li>
                                <li>
                                    We may ask that you provide us with further
                                    information that may help us verify your
                                    identity.
                                </li>
                            </ol>
                            We cannot respond to your request or provide you
                            with personal information if we cannot verify your
                            identity or authority to make the request and
                            confirm that the personal information relates to
                            you. We will only use personal information provided
                            in the request to verify the requestor's identity or
                            authority to make it. At a minimum, your name and
                            email address will be needed in your request. You do
                            not need to create an account with us to submit a
                            request.
                            <br />
                            However, we do consider requests made through your
                            password-protected account sufficiently verified
                            when the request relates to the personal information
                            associated with that specific account.
                            <br />
                            We will confirm receipt of your request within 10
                            days of receiving it. We will then work to deliver
                            you the requested information within 45 days of
                            receiving the request.
                            <br />
                            For instructions on exercising your sale opt-out or
                            opt-in rights, see Right to Opt-Out. We will respond
                            to most consumer requests within 30-45 days of
                            receipt, depending upon where you reside. However,
                            some requests may take longer. We will notify you in
                            writing if we need more time to respond. Normally,
                            we do not charge a fee to process or respond to
                            consumer requests. However, for residents of certain
                            states or countries, we may charge a fee if the
                            request is excessive, repetitive, or manifestly
                            unfounded. We do not charge a fee to process or
                            respond to your verifiable consumer request unless
                            it is excessive, repetitive, or manifestly
                            unfounded. If we determine that the request warrants
                            a fee, we will tell you why we made that decision
                            and provide you with a cost estimate before
                            completing your request.
                        </li>
                    </ol>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        Security
                    </h3>
                    <p><strong>How We Are Protecting Your Data</strong></p>
                    <ul>
                        <li>
                            We have implemented measures designed to secure your
                            personal information from accidental loss and from
                            unauthorized access, use, alteration, and
                            disclosure.
                        </li>
                        <li>
                            All information you provide to us is stored on our
                            secure servers behind firewalls.
                        </li>
                        <li>
                            All payment transactions will be encrypted using SSL
                            technology.
                        </li>
                        <li>
                            The safety and security of your information also
                            depends on you. Where we have given you (or where
                            you have chosen) a password for access to certain
                            parts of our Website or Services, you are
                            responsible for keeping this password confidential.
                            We ask you not to share your password with anyone.
                            We urge you to be careful about giving out
                            information in any public areas of the Website. The
                            information you share in public areas may be viewed
                            by any user of the Website.
                        </li>
                        <li>
                            Unfortunately, the transmission of information via
                            the internet is not completely secure. Although we
                            do our best to protect your personal information, we
                            cannot guarantee the security of your personal
                            information transmitted to our Website. Any
                            transmission of personal information is at your own
                            risk. We are not responsible for circumvention of
                            any privacy settings or security measures contained
                            on the Website.
                        </li>
                    </ul>

                    <p>
                        <strong>Length of Retention and When Destroyed</strong>
                    </p>
                    <ul>
                        <li>
                            We will only retain your information for as long as
                            it is necessary for the purposes that it was
                            processed.
                        </li>
                        <li>
                            If you maintain a profile on our Website we will
                            retain your data for as long as your profile remains
                            active, or until you exercise your rights described
                            herein.
                        </li>
                        <li>
                            We may decide to keep your data in perpetuity if it
                            can no longer identify you individually.
                        </li>
                    </ul>
                    <hr
                        class="my-8 h-px bg-gray-200 border-0 dark:bg-gray-700"
                    />
                </section>

                <section class="mb-8">
                    <h3 class="text-lg text-gray-800 font-bold mb-3">
                        Redress
                    </h3>
                    <p><strong>Changes to Our Privacy Policy</strong></p>
                    <p>
                        It is our policy to post any changes we make to our
                        privacy policy on this page with a notice that the
                        privacy policy has been updated on the Website home
                        page. If we make material changes to how we treat our
                        users' personal information, we will notify you through
                        a notice on the Website home page and via email. The
                        date the privacy policy was last revised is identified
                        at the top of the page. You are responsible for ensuring
                        we have an up-to-date active and deliverable email
                        address for you, and for periodically visiting our
                        Website and this privacy policy to check for any
                        changes.
                    </p>
                    <p><strong>How To Enforce Our Policy</strong></p>
                    <p>
                        To ask questions or comment about this privacy policy
                        and our privacy practices, or to exercise any of your
                        rights described herein, contact us at:
                    </p>
                    <p>
                        contact@10xtravel.com or (330) 763-1915 or write to us at
                        6605 Longshore St, Suite 240 #224,Dublin, OH 43017
                    </p>
                </section>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Head } from '@inertiajs/vue3';
</script>
